import { isPlatformServer } from '@angular/common';
import { HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID, REQUEST } from '@angular/core';
import { environment } from '@env';

/**
 * SSR header-forwarding interceptor.
 *
 * During server-side rendering, outgoing HTTP calls made by Angular services
 * (e.g. `VerifyCharityDomain` → `/charity/info`) run in the Node runtime and
 * lose the context of the original browser request. Two pieces need to be
 * reconstructed for tenant-aware backends to return the right data:
 *
 *  1. **Credentials** — without the browser's cookies attached, the backend
 *     treats every SSR request as unauthenticated and returns 403 for
 *     user-scoped resources. We copy `Cookie` + `Authorization` from the
 *     inbound request.
 *  2. **Original tenant host** — the outgoing request's URL resolves to
 *     `api-dev.givebrite.com`, not the charity host the user was visiting,
 *     so the backend's Host-based tenant routing can't fire. We copy the
 *     inbound `host` header into `X-Forwarded-Host` so a tenant-aware
 *     backend can use it to resolve the correct charity. If the backend
 *     ignores the header, this is a no-op.
 *
 * Forwarding is gated on an allow-list derived from `environment.endpoints`
 * so third-party hosts (analytics, CDNs, blog feeds) never receive
 * credentials or tenant hints.
 *
 * On the browser the interceptor is a no-op — the platform already attaches
 * cookies, and the real Host header is already correct.
 */

const ALLOWED_HOSTS: ReadonlySet<string> = (() => {
  const hosts = new Set<string>();
  const collect = (raw: unknown) => {
    if (typeof raw !== 'string' || !raw) return;
    try {
      hosts.add(new URL(raw).host);
    } catch {
      /* non-URL values (keys, flags) are ignored */
    }
  };
  const endpoints = (environment as any)?.endpoints ?? {};
  for (const value of Object.values(endpoints)) collect(value);
  collect((environment as any)?.endpoint);
  collect((environment as any)?.socket_endpoint);
  return hosts;
})();

export const ssrCookieForwardInterceptor: HttpInterceptorFn = (req, next) => {
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformServer(platformId)) return next(req);

  const request = inject(REQUEST, { optional: true });
  if (!request) return next(req);

  let targetHost: string | null = null;
  try {
    targetHost = new URL(req.url, 'http://localhost').host;
  } catch {
    return next(req);
  }
  if (!ALLOWED_HOSTS.has(targetHost)) return next(req);

  const setHeaders: Record<string, string> = {};
  const cookie = request.headers.get('cookie');
  if (cookie && !req.headers.has('Cookie')) {
    setHeaders['Cookie'] = cookie;
  }
  const authorization = request.headers.get('authorization');
  if (authorization && !req.headers.has('Authorization')) {
    setHeaders['Authorization'] = authorization;
  }
  const forwardedHost = request.headers.get('x-forwarded-host') ?? request.headers.get('host');
  if (forwardedHost && !req.headers.has('X-Forwarded-Host')) {
    setHeaders['X-Forwarded-Host'] = forwardedHost;
  }

  if (Object.keys(setHeaders).length === 0) return next(req);
  return next(req.clone({ setHeaders }));
};
